import { apiFetchWithRetry } from '@/lib/apiFetch';

export interface PageMetadataResponse {
  id: number;
  pagePath: string;
  title?: string;
  metaDescription?: string;
  keywords?: string;
  ogImageUrl?: string;
}

export interface BlogPostSummaryResponse {
  id: number;
  title: string;
  slug: string;
  status: string;
  authorName?: string;
  publishedAt?: string;
  createdAt: string;
  tags: string[];
}

export interface BlogPostListResponse {
  posts: BlogPostSummaryResponse[];
  nextCursor?: string;
  hasMore: boolean;
}

export interface BlogPostResponse {
  id: number;
  title: string;
  slug: string;
  body?: string;
  excerpt?: string;
  featuredImage?: string;
  authorName?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImageUrl?: string;
  status: string;
  publishedAt?: string;
  tags: string[];
}

export async function fetchPageMetadata(path: string): Promise<PageMetadataResponse | null> {
  const encodedPath = encodeURIComponent(path);
  const response = await apiFetchWithRetry(`/api/v1/seo/page-metadata?path=${encodedPath}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`Failed to fetch page metadata: ${response.status}`);
  return response.json();
}

export async function fetchBlogPosts(params: {
  tag?: string;
  cursor?: string;
  size?: number;
} = {}): Promise<BlogPostListResponse> {
  const searchParams = new URLSearchParams();
  if (params.tag) searchParams.set('tag', params.tag);
  if (params.cursor) searchParams.set('cursor', params.cursor);
  if (params.size !== undefined) searchParams.set('size', String(params.size));

  const query = searchParams.toString();
  const url = `/api/v1/seo/blog/posts${query ? `?${query}` : ''}`;

  const response = await apiFetchWithRetry(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) throw new Error(`Failed to fetch blog posts: ${response.status}`);
  return response.json();
}

export async function fetchBlogPost(slug: string): Promise<BlogPostResponse | null | 'GONE'> {
  const response = await apiFetchWithRetry(`/api/v1/seo/blog/posts/${encodeURIComponent(slug)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (response.status === 404) return null;
  if (response.status === 410) return 'GONE';
  if (!response.ok) throw new Error(`Failed to fetch blog post: ${response.status}`);
  return response.json();
}
